import { AuthLayout } from '@beatq/ui';
import { AccountMenu } from '@beatq/shared';
import { Link, useLocation } from 'react-router-dom';
import { ClipboardCheck, CreditCard, Store } from 'lucide-react';
import { ONBOARDING_CHECKLIST_STEPS, ONBOARDING_PHASES } from '../features/onboarding/catalog.js';

const PHASE_PATHS: Record<string, string[]> = {
  business: ['/business-details', '/commercial-terms'],
  payments: [
    '/beat-pay-setup',
    '/beat-pay-return',
    '/verification-upload',
    '/verification-status',
    '/verification-rejected',
    '/payout-setup',
  ],
  'store-setup': [
    '/create-branch',
    '/opening-hours',
    '/service-area',
    '/fulfilment-settings',
    '/product-list',
    '/product-editor',
    '/bulk-import',
  ],
  'review-launch': ['/branch-status'],
};

export function OnboardingPhaseNav({ currentPhaseIndex }: { currentPhaseIndex: number }) {
  return (
    <nav aria-label="Onboarding phases" className="onboarding-shell-phases" tabIndex={0}>
      <ol>
        {ONBOARDING_PHASES.map((phase, index) => {
          // Position in the journey is not proof that an earlier milestone is complete.
          // The checklist and activation readiness APIs own completion status.
          const state =
            index < currentPhaseIndex
              ? 'previous'
              : index === currentPhaseIndex
                ? 'current'
                : 'upcoming';
          return (
            <li
              key={phase.key}
              className={`is-${state}`}
              data-phase-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className="onboarding-shell-phase-marker" aria-hidden="true">
                {index + 1}
              </span>
              <span className="onboarding-shell-phase-label">{phase.label}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Focused setup shell for SCR-MER-001–008 onboarding surfaces. */
export function MerchantOnboardingAuthLayout() {
  const location = useLocation();
  const isChecklist = location.pathname === '/onboarding-checklist';
  const showProgress =
    location.pathname !== '/merchant-sign-up' && location.pathname !== '/merchant-sign-in';
  const currentPhaseIndex = ONBOARDING_PHASES.findIndex((phase) =>
    PHASE_PATHS[phase.key]?.includes(location.pathname),
  );
  const currentPhase = ONBOARDING_PHASES[currentPhaseIndex];
  const currentStep = ONBOARDING_CHECKLIST_STEPS.find(
    (step) =>
      location.pathname === step.route ||
      (step.code === 'catalogue' &&
        ['/product-editor', '/bulk-import'].includes(location.pathname)),
  );
  const currentStepLabel =
    location.pathname === '/product-editor'
      ? 'Add a product'
      : location.pathname === '/bulk-import'
        ? 'Import your menu'
        : currentStep?.label;
  const brandContent =
    showProgress && !isChecklist && currentPhase ? (
      <div className="onboarding-journey-rail">
        <p className="onboarding-rail-eyebrow">In this stage</p>
        <p className="onboarding-rail-title">{currentPhase.label}</p>
        <nav aria-label={`${currentPhase.label} tasks`}>
          <ol className="onboarding-rail-tasks">
            {currentPhase.stepCodes.map((code) => {
              const task = ONBOARDING_CHECKLIST_STEPS.find((step) => step.code === code);
              return task ? (
                <li key={code} aria-current={currentStep?.code === code ? 'step' : undefined}>
                  {task.label}
                </li>
              ) : null;
            })}
          </ol>
        </nav>
        <Link to="/onboarding-checklist" className="onboarding-rail-checklist-link">
          Full checklist <span aria-hidden="true">→</span>
        </Link>
      </div>
    ) : !showProgress ? (
      <div className="onboarding-welcome-panel">
        <div className="onboarding-welcome-art" aria-hidden="true">
          <div className="onboarding-welcome-art-card">
            <Store size={42} strokeWidth={1.6} />
          </div>
          <span className="onboarding-welcome-art-dot" />
        </div>
        <p className="onboarding-rail-eyebrow">BeatQ Eats Merchant</p>
        <p className="onboarding-welcome-title">Bring your business to more local customers.</p>
        <p className="onboarding-welcome-copy">
          Create your account in a few minutes, then complete payments, your first branch and your
          menu from one guided workspace.
        </p>
        <ul className="onboarding-welcome-benefits">
          <li>
            <ClipboardCheck size={20} aria-hidden="true" />
            <span>One clear checklist from sign-up to launch</span>
          </li>
          <li>
            <CreditCard size={20} aria-hidden="true" />
            <span>Secure payment setup with BeatQ Pay</span>
          </li>
          <li>
            <Store size={20} aria-hidden="true" />
            <span>Tools for one branch or many</span>
          </li>
        </ul>
        <p className="onboarding-welcome-footnote">
          Built for restaurants, grocers and local retailers.
        </p>
      </div>
    ) : undefined;

  return (
    <>
      <header
        className="onboarding-shell-header"
        data-progress-visible={showProgress}
        data-onboarding-page={isChecklist ? 'checklist' : undefined}
      >
        <Link
          to={showProgress ? '/onboarding-checklist' : '/merchant-sign-up'}
          className="onboarding-shell-brand"
          aria-label="BeatQ Eats merchant setup"
        >
          <strong>BeatQ Eats</strong>
          <span>Merchant setup</span>
        </Link>
        {showProgress ? (
          <OnboardingPhaseNav currentPhaseIndex={currentPhaseIndex} />
        ) : (
          <p className="onboarding-shell-welcome">Set up your business</p>
        )}
        <AccountMenu avatarOnly signOutLabel="Sign out of onboarding" />
        {showProgress && !isChecklist && currentStepLabel ? (
          <div className="onboarding-shell-substep">
            <span>
              Stage {currentPhaseIndex + 1} of {ONBOARDING_PHASES.length}
            </span>
            <strong>{currentStepLabel}</strong>
            <Link to="/onboarding-checklist">Checklist</Link>
          </div>
        ) : null}
      </header>
      <AuthLayout
        surface="merchant"
        contentAlign="start"
        formMaxWidth="2xl"
        onboarding
        tagline="Complete your business, payments and store setup in one place."
        brandContent={brandContent}
      />
    </>
  );
}
